import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";

const MAILERLITE_ACTION =
  "https://assets.mailerlite.com/jsonp/2208570/forms/182464527424554064/subscribe";

export default function CTASection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isDark } = useTheme();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("ml-submit", "1");
      body.append("anticsrf", "true");
      await fetch(MAILERLITE_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`py-8 px-8 lg:px-16 pb-20 transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto">
        <div className={`grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden border ${isDark ? "border-white/5" : "border-black/8"}`}>
          {/* Left: Visual */}
          <div className={`relative min-h-[500px] overflow-hidden ${isDark ? "bg-[#0a0a0a]" : "bg-[#f5f5f5]"}`}>
            <div className="absolute inset-0 flex flex-col justify-between p-10 lg:p-12">
              <span className={`font-inter text-xs uppercase tracking-[0.2em] ${isDark ? "text-[#555555]" : "text-[#999999]"}`}>
                Join 800+ Resellers
              </span>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  {[
                    { label: "Sale Price", value: "$120.00", sub: false },
                    { label: "Platform Fees", value: "–$15.90", sub: true },
                    { label: "Payment Processing", value: "–$3.57", sub: true },
                    { label: "Shipping", value: "–$8.50", sub: true },
                  ].map((row) => (
                    <div key={row.label} className={`flex items-center justify-between border-b py-3 ${row.sub ? "opacity-60" : ""} ${isDark ? "border-white/4" : "border-black/5"}`}>
                      <span className={`font-inter text-sm ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>{row.label}</span>
                      <span className={`font-inter text-sm ${row.sub ? (isDark ? "text-[#555555]" : "text-[#777777]") : (isDark ? "text-white" : "text-[#111111]")}`}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className={`border rounded-xl px-5 py-4 flex items-center justify-between mt-2 ${isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/8"}`}>
                  <span className={`font-inter text-sm ${isDark ? "text-[#888888]" : "text-[#555555]"}`}>Your Real Profit</span>
                  <span className={`font-syne font-bold text-2xl ${isDark ? "text-white" : "text-[#111111]"}`}>$47.03</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Email collection */}
          <div className={`p-10 lg:p-14 flex flex-col justify-center ${isDark ? "bg-[#0d0d0d]" : "bg-[#f0f0f0]"}`}>
            <h3 className={`font-syne font-bold text-4xl lg:text-5xl leading-tight mb-4 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Know your margins <br />
              <span className={`text-transparent bg-clip-text ${isDark ? "bg-gradient-to-r from-white to-[#555555]" : "bg-gradient-to-r from-[#111111] to-[#888888]"}`}>
                before you flip.
              </span>
            </h3>
            <p className={`font-inter text-base leading-relaxed mb-8 max-w-sm ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
              Trackly is in early access. Sign up now to get instant access to the Flip Calculator in your beta dashboard — and lock in free access at launch.
            </p>

            {submitted ? (
              <div className={`border rounded-xl p-5 mb-6 ${isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/8"}`}>
                <div className="flex items-center gap-3">
                  <i className={`ri-check-line ${isDark ? "text-white" : "text-[#111111]"}`} />
                  <p className={`font-inter text-sm ${isDark ? "text-white" : "text-[#111111]"}`}>You&apos;re on the list! We&apos;ll email you when Trackly launches.</p>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="early-access-cta-form"
                action={MAILERLITE_ACTION}
                method="post"
                onSubmit={handleSubmit}
                className="flex flex-col gap-3 mb-6"
              >
                <input type="hidden" name="ml-submit" value="1" />
                <input type="hidden" name="anticsrf" value="true" />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full font-inter font-medium text-sm px-8 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    isDark
                      ? "bg-white text-black hover:bg-[#e8e8e8]"
                      : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
                  }`}
                >
                  {submitting ? "Joining..." : "Get Early Access — It's Free"}
                  {!submitting && <i className="ri-arrow-right-line" />}
                </button>
              </form>
            )}

            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {["No credit card needed", "Free at launch", "Cancel anytime"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <i className={`ri-check-line text-xs ${isDark ? "text-[#666666]" : "text-[#888888]"}`} />
                  <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>{item}</span>
                </div>
              ))}
            </div>

            {/* Social handles */}
            <div className={`flex items-center gap-6 mt-8 pt-8 border-t ${isDark ? "border-white/5" : "border-black/6"}`}>
              <span className={`font-inter text-xs whitespace-nowrap ${isDark ? "text-[#444444]" : "text-[#bbbbbb]"}`}>Follow along</span>
              <div className="flex items-center gap-3">
                {[
                  { icon: "ri-twitter-x-fill", label: "X (Twitter)", href: "https://x.com/usetrackly", imgSrc: "https://storage.readdy-site.link/project_files/d2fca44a-eb94-4797-9168-15f21f7a5589/7c4a3f62-9d76-40b6-9e43-3fce62dc6c6e_X-Logo.png?v=9928d9c65f8e3db4a5a51f88e3be6a7d", invertOnDark: true, imgSize: "w-5 h-5" },
                  { icon: "ri-instagram-fill", label: "Instagram", href: "https://instagram.com/usetrackly", imgSrc: "https://static.readdy.ai/image/1de166adf1bbf13a9e4054af96cf4ae8/907fda1763aab42155461943ef6de0d8.webp", invertOnDark: false, imgSize: "w-4 h-4" },
                  { icon: "ri-tiktok-fill", label: "TikTok", href: "https://tiktok.com/@usetrackly", imgSrc: "https://storage.readdy-site.link/project_files/d2fca44a-eb94-4797-9168-15f21f7a5589/68f04236-6d28-4cf2-a183-0dc76966705b_tiktok-logo-tikok-icon-transparent-tikok-app-logo-free-png.png?v=f4b9689355e39ba27d0fdaba5b509f5b", invertOnDark: true, imgSize: "w-5 h-5" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="nofollow noreferrer"
                    title={`${s.label} — @usetrackly`}
                    className={`group flex flex-col items-center gap-1.5 cursor-pointer transition-all duration-200`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 ${
                      isDark
                        ? "bg-white/5 border-white/10 group-hover:bg-white/12 group-hover:border-white/20"
                        : "bg-black/5 border-black/10 group-hover:bg-black/10 group-hover:border-black/20"
                    }`}>
                      {s.imgSrc ? (
                        <img
                          src={s.imgSrc}
                          alt={s.label}
                          className={`${s.imgSize ?? "w-4 h-4"} object-contain transition-all duration-200 ${s.invertOnDark && isDark ? "invert" : ""}`}
                        />
                      ) : (
                        <i className={`${s.icon} text-lg ${isDark ? "text-[#888888] group-hover:text-white" : "text-[#777777] group-hover:text-[#111111]"} transition-colors duration-200`} />
                      )}
                    </div>
                    <span className={`font-inter text-[10px] ${isDark ? "text-[#444444] group-hover:text-[#888888]" : "text-[#bbbbbb] group-hover:text-[#777777]"} transition-colors duration-200`}>
                      {s.label === "X (Twitter)" ? "X" : s.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
